import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAbstractProductData,
  fetchAllAbstractProducts,
} from "../api/abstractProduct.api";
import { useState } from "react";

interface UseAbstractProductProps {
  companyId?: number;
  exactMatch?: boolean;
}

const useAbstractProduct = ({
  companyId,
  exactMatch,
}: UseAbstractProductProps) => {
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  const [searchEans, setAbstractSearchEans] = useState<string[]>([]);

  const {
    data: abstractProductData,
    error: abstractProductError,
    isLoading: isLoadingAbstractProduct,
    isSuccess: isAbstractProductSuccess,
  } = useQuery({
    queryKey: ["abstractProduct", searchEans, exactMatch],
    queryFn: () =>
      fetchAbstractProductData(
        bearerToken as string,
        companyId as number,
        1,
        searchEans,
        exactMatch,
      ),
    enabled: searchEans.length > 0 && companyId !== undefined,
  });

  const findAllAbstractProducts = async (identifiers: string[]) => {
    const result = await findAllAbstractProductsMutation.mutateAsync({
      bearerToken: bearerToken as string,
      searchStrings: identifiers,
    });
    return result.data;
  };

  const findAllAbstractProductsMutation = useMutation({
    mutationFn: async ({
      bearerToken,
      searchStrings,
    }: {
      bearerToken: string;
      searchStrings: string[];
    }) => {
      const result = await fetchAllAbstractProducts(bearerToken, searchStrings);
      return result;
    },
    onError: (error) => {
      console.error("Failed to fetch all abstract products:", error);
    },
  });

  const isLoadingAbstractProducts = findAllAbstractProductsMutation.isPending;
  return {
    abstractProductData,
    isLoadingAbstractProduct,
    abstractProductError,
    setAbstractSearchEans,
    isAbstractProductSuccess,
    findAllAbstractProducts,
    isLoadingAbstractProducts,
  };
};

export default useAbstractProduct;
